<script setup lang="ts">
import { computed } from 'vue'

import { useI18n } from '@/shared/i18n'
import { Icon, Menu, type MenuItemOption } from '@/shared/ui'

defineOptions({ name: 'TabActionMenu' })

const props = defineProps<{ tabName: string; canRemove: boolean }>()
const emit = defineEmits<{ rename: []; duplicate: []; remove: [] }>()
const { t } = useI18n()

/**
 * v3 dimmed Delete unconditionally and left it clickable (`CustomDashboardActionMenu.vue:24-31`
 * disables on `key !== 'o-red'`, i.e. the inverse of what it meant), while `deleteTab` refused
 * silently whenever a cell had one tab (`CustomDashboardSection.vue:374`). Two halves of one lie:
 * the control looked disabled when it worked, and looked working when it did nothing. Here the
 * disabled state tracks the real rule, so the menu never offers an action it will not perform.
 */
const items = computed<MenuItemOption[]>(() => [
  { id: 'rename', label: t('dashboards.grid.tabRename') },
  { id: 'duplicate', label: t('dashboards.grid.tabDuplicate') },
  {
    id: 'remove',
    label: t('dashboards.grid.tabDelete'),
    disabled: !props.canRemove,
    danger: true,
  },
])

function onSelect(item: MenuItemOption): void {
  if (item.id === 'rename') emit('rename')
  if (item.id === 'duplicate') emit('duplicate')
  if (item.id === 'remove') emit('remove')
}
</script>

<template>
  <Menu
    :label="t('dashboards.grid.tabActions', { name: tabName })"
    :items="items"
    placement="bottom-end"
    trigger="icon"
    size="xs"
    @select="onSelect"
  >
    <template #trigger><Icon name="ellipsis" :size="14" /></template>
  </Menu>
</template>
